import React, { Component } from 'react';
import compose from 'recompose/compose';
import { withTranslation } from 'react-i18next';

// Material helpers
import { withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CircularProgress from '@material-ui/core/CircularProgress';
import Modal from '@material-ui/core/Modal';
import Backdrop from '@material-ui/core/Backdrop';
import Fade from '@material-ui/core/Fade';
import Button from '@material-ui/core/Button';
import Edit from '@material-ui/icons/Edit';
import Remove from '@material-ui/icons/Remove';
import LockIcon from '@material-ui/icons/Lock';
import TextField from '@material-ui/core/TextField';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

// Calendar & Dates
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import moment from 'moment';
import 'moment/locale/es';
import 'moment/locale/pt';

// Services
import { getDataMaintenance } from '../../../services/getMaintenance';
import { updateMaintenance } from '../../../services/createMaintenance';
import { updateMarkePeriod } from '../../../services/markedPeriod';
import { deleteMarkePeriod } from '../../../services/markedPeriod';

import styles from './styles';

const AgendaHeader = ({ titles }) => (
  <div style={{ 
    display: 'grid', 
    gridTemplateColumns: '1.5fr 1fr 2fr 3.5fr 1fr', // Mismos anchos que el body
    gap: '10px',
    width: '100%',
    fontWeight: 'bold',
    textAlign: 'left'
  }}>
    <span>{titles[0]}</span>
    <span style={{ textAlign: 'center' }}>{titles[1]}</span>
    <span>{titles[2]}</span>
    <span>{titles[3]}</span>
    <span style={{ textAlign: 'right' }}>{titles[4]}</span>
  </div>
);

// 1. Diccionario de Idiomas para los botones
const calendarMessages = {
  es: {
    allDay: 'Todo el día',
    previous: 'Anterior',
    next: 'Siguiente',
    today: 'Hoy',
    month: 'Mes',
    week: 'Semana',
    day: 'Día',
    agenda: 'Agenda',
    showMore: total => `+ Ver más (${total})`,
    date: 'Fecha',  // Specifically for Agenda View header
    time: 'Hora',   // Specifically for Agenda View header
    event: <AgendaHeader titles={['Usuario', 'Estado', 'Titulo', 'Comentario', 'Acciones']} />,
    noEventsInRange: 'No hay eventos en este rango',
  },
  pt: {
    allDay: 'Dia inteiro',
    previous: 'Anterior',
    next: 'Próximo',
    today: 'Hoje',
    month: 'Mês',
    week: 'Semana',
    day: 'Dia',
    agenda: 'Agenda',
    showMore: total => `+ Ver mais (${total})`,
    date: 'Data',  // Specifically for Agenda View header
    time: 'Hora',   // Specifically for Agenda View header
    event: <AgendaHeader titles={['Usuário', 'Status', 'Título', 'Comentário', 'Ações']} />,
    noEventsInRange: 'Não há eventos neste intervalo',
  },
  en: {
    allDay: 'All day',
    previous: 'Back',
    next: 'Next',
    today: 'Today',
    month: 'Month',
    week: 'Week',
    day: 'Day',
    agenda: 'Agenda',
    date: 'Date',  // Specifically for Agenda View header
    time: 'Hour',   // Specifically for Agenda View header
    event: <AgendaHeader titles={['User', 'Status', 'Title', 'Comment', 'Actions']} />,
    noEventsInRange: 'There are no events in this range',
  }
};

const mapToMomentLocale = (lng) => {
  if (!lng) return 'es';
  return lng.split('-')[0];
};

const localizer = momentLocalizer(moment);

// 2. Componente de Evento Optimizado
const CustomEvent = ({ event }) => {
  const startTime = moment(event.start).format('HH:mm');
  const endTime = moment(event.end).format('HH:mm');
  return (
    <div style={{
        backgroundColor: event.backgroundColor || '#3174ad', 
        height: '100%',
        padding: '4px 8px',
        borderRadius: '4px',
        color: 'white',
        fontSize: '12px',
        boxSizing: 'border-box',
        overflow: 'hidden'
    }}>
      <div style={{ fontWeight: 'bold', fontSize: '10px', opacity: 0.9 }}>
        {startTime} - {endTime}
      </div>
      <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
        {event.title} 
      </div>
    </div>
  );
};

const CustomAgendaEvent = ({ event, onEdit, onDelete, t }) => {
  const esEditable = event.editable;
  return (
    <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1.5fr 1fr 2fr 3.5fr 1fr', // IDÉNTICO AL HEADER
        gap: '10px', 
        alignItems: 'center', 
        width: '100%',
        fontSize: '12px'
    }}>
      {/* Usuario */}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {event.usuario || '---'}
      </span>

      {/* Estado */}
      <div style={{ textAlign: 'center' }}>
        <span style={{fontSize: '10px', backgroundColor: '#eee', padding: '2px 4px', borderRadius: '4px', border: '1px solid '+event.backgroundColor }}>
          {t('history.status.option', {returnObjects: true})?.[event.estado] ?? event.estado}
        </span>
      </div>

      {/* Título */}
      <span style={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {event.title}
      </span>

      {/* Comentario */}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
        {event.comentario || '---'}
      </span>

      {/* Acciones Condicionales */}
      <div style={{ textAlign: 'right' }}>
        {esEditable ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '5px' }}>
            <button 
              onClick={(e) => { 
                e.stopPropagation(); // <--- EVITA QUE EL CALENDARIO DISPARE OTROS EVENTOS
                onEdit(event);      // <--- LLAMA A LA FUNCIÓN QUE ABRE EL MODAL
              }} 
              style={{ cursor: 'pointer', border: 'none', background: 'none', padding: 0 }}
            >
              <Edit style={{ fontSize: '18px', color: '#1976d2' }}/>
            </button>
            <button 
              onClick={(e) => { 
                e.stopPropagation(); 
                onDelete(event); 
              }} 
              style={{ cursor: 'pointer', border: 'none', background: 'none', padding: 0, color: '#d32f2f' }}
              title="Eliminar"
            >
              <Remove style={{ fontSize: '18px' }}/>
            </button>
          </div>
        ) : (
          /* Opcional: Mostrar un candado o simplemente dejar el espacio vacío */
          <span style={{ color: '#ccc', fontSize: '10px' }}><LockIcon/></span>
        )}
      </div>
    </div>
  );
};

// Este componente SÍ recibe el event
const CustomAgendaTime = ({ event }) => {
  if (!event) return null;

  // Formato: "15 May 10:00 - 17 May 18:00"
  const start = moment(event.start).format('DD MMM HH:mm');
  const end = moment(event.realEnd || event.end).format('DD MMM HH:mm');
  
  return (
    <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap', fontSize: '12px' }}>
      {start} — {end}
    </div>
  );
};


const eventStyleGetter = () => ({
  style: {
    backgroundColor: 'transparent',
    border: 'none',
    padding: '0'
  }
});

class Activities extends Component {
  constructor(props) {
    super(props);
    const initialLocale = mapToMomentLocale(props.i18n?.language);
    moment.locale(initialLocale);

    this.state = {
      load: true,
      dataError: false,
      events: [],
      begin: new Date(), 
      currentLanguage: initialLocale,
      eventSelected: null,
      dialogOpen: false,
      boolean:false,
      currentView: 'week', 
      row: {
        user: '',
        begin: '',
        final: '',
        titulo: '',
        comentario: '',
        usuario_id:'',
        editable: '',
        periodoMarcado: '',
        objId: '', 
        nodoId: ''
      },
      showDetail: false, // Para el modal de "Solo lectura"
      showEdit: false,   // Para el modal de "Edición"
      snackbar: {
        open: false,
        message: '',
        severity: 'info' // 'success', 'error', 'warning', 'info'
      },
      dialogOpenMsgDelete: false,
      rowToDelete: null,
    };
  }

  componentDidMount() {
    this.getDataFromAPI();
  }

  componentDidUpdate(prevProps, prevState) {
    const currentLang = this.props.i18n?.language;
    /*if(prevState.boolean!== this.state.boolean){
      this.getDataFromAPI()
    }*/
    if (currentLang !== prevProps.i18n?.language) {
      const newLocale = mapToMomentLocale(currentLang);
      moment.locale(newLocale);
      this.setState({ currentLanguage: newLocale });
    }
  }

  getDataFromAPI = () => {
    this.setState({ load: true });
    let token = sessionStorage.getItem('key');
    let user = sessionStorage.getItem('user');
    let client = sessionStorage.getItem('client_id');
    let objetivos = JSON.parse(sessionStorage.getItem('listaObjetivosOnline'));
    const { begin } = this.state;
    
    let dateFormatted = moment(begin).subtract(3, 'months').format('YYYY-MM-DD');
    var newObjetivos=(objetivos.map(ele=>ele.objetivo_id))

    getDataMaintenance(client, token, dateFormatted, user, newObjetivos, 0)
      .then(data => {
        this.processData(data);
      })
      .catch(() => {
        this.setState({ dataError: true, load: false });
      });
  }

  processData(data){
    let user = sessionStorage.getItem('user');
    let admin = sessionStorage.getItem('admin_id');
    var listObjectives=JSON.parse(sessionStorage.getItem("listaObjetivosOnline"))
    var newList=[]
    data.map(ele=>{
      var valueOK=false
      ele.objetivo_id.map(obj=>{
        if(listObjectives.filter(i=>i.objetivo_id===obj).length>0){
          valueOK=true
        }
      })
      if(valueOK){
        const editable = ele.estado?.toLowerCase() === 'ingresado' && 
                      (parseInt(user) === parseInt(ele.usuario_id) || Boolean(admin));
        const periodoMarcado = Boolean(ele.periodo_marcado_id);
        var start = new Date(ele.fecha_inicio)
        var end =new Date(ele.fecha_termino)
        var value = ele.titulo
        var backgroundColor = ele.backgroundColor
        newList.push({id:ele.id, start:start, end:end, realEnd: end, title:value, estado:ele.estado, 
          usuario:ele.nombre,comentario:ele.comentario, objetivos:ele.objetivos, backgroundColor:backgroundColor, 
          usuario_id:ele.usuario_id, editable: editable, periodoMarcado:periodoMarcado, admin: admin,
          objId: ele.objetivo_id, nodoId:ele.nodos_id})
      }
      
    })

    this.setState({
      load: false,
      events:newList
    });
  }
  // Captura el cambio de vista
  handleViewChange = (view) => {
    this.setState({ currentView: view });
  };

  handleNavigate = (newDate) => {
    this.setState(prevState=>({
      begin:moment(newDate),
      //boolean:!prevState.boolean,
      //load:true
    }));
  };

  // Se dispara al hacer click en el evento (Calendario normal)
  handleOpenDetail = (event) => {
    this.setState({
      eventSelected: event,
      showDetail: true
    });
  };

  // Se dispara SOLO desde el botón Edit (Agenda)
  handleOpenEdit = (event) => {
    this.setState({
      eventSelected: event,
      row: {
        id: event.id,
        user: event.usuario || '',
        begin: moment(event.start).format('YYYY-MM-DD HH:mm'),
        final: moment(event.realEnd || event.end).format('YYYY-MM-DD HH:mm'),
        titulo: event.title || '',
        comentario: event.comentario || '',
        usuario_id: event.usuario_id,
        editable: event.editable,
        periodoMarcado: event.periodoMarcado,
        objId: event.objId, 
        nodoId:event.nodoId
      },
      showEdit: true
    });
  };

  // Función para cerrar ambos
  handleCloseModals = () => {
    this.setState({
      showDetail: false,
      showEdit: false
    });
  };

  renderModalContent = () => {
    const { eventSelected, showDetail } = this.state;
    const { classes, t } = this.props;

    if (!eventSelected) return null;

    return (
      <Modal
      open={showDetail}
      onClose={this.handleCloseModals}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Fade in={showDetail}>
        <div className={classes.paper} style={{ 
          backgroundColor: 'white', 
          padding: '20px', 
          outline: 'none', 
          borderRadius: '8px',
          maxWidth: '500px',
          margin: 'auto',
          marginTop: '10%'
        }}>
          <Grid item xs={12} style={{ textAlign: "right" }}>
              <IconButton onClick={this.handleCloseModals}>
                <CloseIcon />
              </IconButton>
            </Grid>
  
          <Grid container spacing={2}>
            <Grid item xs={4}><Typography color="textSecondary">{t ? t('maintenance.activities.modal.usuario') : 'Usuario: '}</Typography></Grid>
            <Grid item xs={8}><Typography>{eventSelected.usuario}</Typography></Grid>
            
            <Grid item xs={4}><Typography color="textSecondary">{t ? t('maintenance.activities.modal.estado') : 'Estado: '}</Typography></Grid>
            <Grid item xs={8}><Typography>{eventSelected.estado}</Typography></Grid>
            
            <Grid item xs={4}><Typography color="textSecondary">{t ? t('maintenance.activities.modal.titulo') : 'Titulo: '}</Typography></Grid>
            <Grid item xs={8}><Typography>{eventSelected.title}</Typography></Grid>
            
            <Grid item xs={4}><Typography color="textSecondary">{t ? t('maintenance.activities.modal.inicio') : 'Inicio: '}</Typography></Grid>
            <Grid item xs={8}><Typography>{moment(eventSelected.start).format('YYYY-MM-DD HH:mm:ss')}</Typography></Grid>
            
            <Grid item xs={4}><Typography color="textSecondary">{t ? t('maintenance.activities.modal.termino') : 'Término: '}</Typography></Grid>
            <Grid item xs={8}><Typography>{moment(eventSelected.realEnd).format('YYYY-MM-DD HH:mm:ss')}</Typography></Grid>

            <Grid item xs={12}>
              <Typography color="textSecondary">{t ? t('maintenance.activities.modal.monitoreos') : 'Monitoreos: '}</Typography>
              <div style={{ marginLeft: '10px' }}>
                {eventSelected.objetivos.map((obj, i) => (
                  <Typography key={i} variant="body2">• {obj.nombre}</Typography>
                ))}
              </div>
            </Grid>
          </Grid>
        </div>
      </Fade>
    </Modal>
      
    );
  };

  // Maneja los cambios en los inputs del modal
  handleFieldChange = (field, value) => {
    this.setState(prevState => ({
      row: {
        ...prevState.row,
        [field]: value
      }
    }));
  };

  // Función para guardar los cambios
  handleSaved = async (eventTarget, nuevoEstado = null) => {
    const dataRow = eventTarget || this.state.row;
    const { t } = this.props;
    let token = sessionStorage.getItem('key');
    let id_user = sessionStorage.getItem('user');
    let timezone = sessionStorage.getItem('tz')
    let admin = sessionStorage.getItem('admin_id');
    // 1. Validación de Usuario
    if (dataRow.periodoMarcado) {
      if (nuevoEstado){
        deleteMarkePeriod({dataRow}).then(response=>{
          if(response.status !== 200){
            this.showNotification(
              t ? t('history.msg.updateFailed') : 'No fue posible llevar a cabo la modificación',
              'error'
            );
          }else{
            this.showNotification(
              t ? t('history.msg.updateSuccess') : 'Cambio exitoso',
              'success'
            );
        }})
      } else {
        var begin=moment(dataRow.begin).format("YYYY-MM-DD HH:mm:SS")
        var final=moment(dataRow.final).format("YYYY-MM-DD HH:mm:SS")
        updateMarkePeriod({periodo_marcado_id: dataRow.id,
                                    fecha_inicio: begin,
                                    fecha_termino: final,
                                    motivo: dataRow.titulo,
                                    tz: timezone,
                                    usuario: admin,
                                    objId: dataRow.objId,
                                    nodoId: dataRow.nodoId
                                }).then(response=>{
                                    if(response.status !== 200){
                                      this.showNotification(
                                        t ? t('history.msg.updateFailed') : 'No fue posible llevar a cabo la modificación',
                                        'error'
                                      );
                                    }else{
                                      this.showNotification(
                                        t ? t('history.msg.updateSuccess') : 'Cambio exitoso',
                                        'success'
                                      );
                                }})
      }
    } else {
      // 2. Llamada a la API
      updateMaintenance(dataRow.id, id_user, token, dataRow.comentario, dataRow.titulo, nuevoEstado)
      .then(data => {
        if (data === false) {
          this.showNotification(
            t ? t('history.msg.updateFailed') : 'No fue posible llevar a cabo la modificación',
            'error'
          );
        } else {
          this.showNotification(
            t ? t('history.msg.updateSuccess') : 'Cambio exitoso',
            'success'
          );
          this.getDataFromAPI(); // Refrescar el calendario para ver los cambios
        }
      })
      .catch(() => {
        this.showNotification('Error de conexión con el servidor', 'error');
      });
    }  
    this.handleCloseModals();
  };

  // Función auxiliar para activar el snackbar
  showNotification = (msg, severity) => {
    this.setState({
      snackbar: {
        open: true,
        message: msg,
        severity: severity
      }
    });
  };
  handleCloseSnackbar = () => {
    this.setState(prev => ({ snackbar: { ...prev.snackbar, open: false } }));
  };

  renderEditModal = () => {
    const { showEdit, row } = this.state;
    const { t, classes } = this.props;
    const isDateError = row.begin && row.final && new Date(row.final) <= new Date(row.begin);
    return (
      <Modal
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        open={showEdit}
        onClose={this.handleCloseModals}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        disableAutoFocus={true}
        disableEnforceFocus={true}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={showEdit}>
          {/* Ajustado el Grid para que funcione como contenedor del modal */}
          <Grid
            container
            spacing={2}
            style={{ 
              backgroundColor: "white", 
              padding: '20px', 
              maxWidth: '400px', 
              borderRadius: '8px',
              outline: 'none'
            }}
          >
            <Grid item xs={12} style={{ textAlign: "right" }}>
              <IconButton onClick={this.handleCloseModals}>
                <CloseIcon />
              </IconButton>
            </Grid>
  
            <Grid item xs={12} style={{ textAlign: "center" }}>
              <TextField
                fullWidth
                label={t ? t('history.modal.label.nombre') : 'Nombre'}
                margin="dense"
                value={row.user}
                disabled
              />
            </Grid>
  
            <Grid item xs={12} style={{ textAlign: "center" }}>
              <TextField
                label={t ? t('history.modal.label.inicio') : 'inicio'}
                margin="dense"
                onChange={event => {
                  const rawValue = event.target.value;
                  // Validamos que el usuario haya seleccionado una fecha completa antes de formatear
                  const formattedValue = rawValue ? moment(rawValue).format("YYYY-MM-DD HH:mm:ss") : "";
                  
                  this.handleFieldChange('begin', formattedValue);
                }}
                type="datetime-local"
                required
                value={row.begin ? moment(row.begin).format("YYYY-MM-DDTHH:mm") : ""}
                //disabled={!row.periodoMarcado}
                disabled
                id="standard-disabled"
              />
            </Grid>
  
            <Grid item xs={12} style={{ textAlign: "center" }}>
              <TextField
                label={t ? t('history.modal.label.termino') : 'termino'}
                margin="dense"
                onChange={event => {
                  const rawValue = event.target.value;
                  // Validamos que el usuario haya seleccionado una fecha completa antes de formatear
                  const formattedValue = rawValue ? moment(rawValue).format("YYYY-MM-DD HH:mm:ss") : "";
                  
                  this.handleFieldChange('final', formattedValue);
                }}
                
                type="datetime-local"
                required
                value={row.final ? moment(row.final).format("YYYY-MM-DDTHH:mm") : ""}
                //disabled={!row.periodoMarcado}
                disabled
                id="standard-disabled"
                // Si hay error, se pone en rojo y muestra el mensaje
                error={isDateError}
                helperText={isDateError ? (t ? t('history.helperText.isDateError') : "La fecha de término debe ser posterior al inicio") : ""}
                // Atributo nativo para que el calendario del navegador no deje elegir fechas anteriores
                inputProps={{ min: row.begin }}
              />
            </Grid>
  
            <Grid item xs={12} style={{ textAlign: "center" }}>
              <TextField
                fullWidth
                label={t ? t('history.modal.label.titulo') : 'Título'}
                margin="dense"
                onChange={event => this.handleFieldChange('titulo', event.target.value)}
                required
                value={row.titulo}
              />
            </Grid>
  
            <Grid item xs={12} style={{ textAlign: "center" }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t ? t('history.modal.label.comentario') : 'Comentario'}
                margin="dense"
                onChange={event => this.handleFieldChange('comentario', event.target.value)}
                required
                disabled={row.periodoMarcado}
                value={row.comentario}
              />
            </Grid>
  
            <Grid item xs={12} style={{ textAlign: "center", marginTop: '20px' }}>
              <Button
                onClick={() => this.handleSaved(null, null)}
                color="primary"
                variant="contained"
                fullWidth
                disabled={isDateError || !row.begin || !row.final}
              >
                {t ? t('history.modal.save') : 'Guardar Mantenimiento'}
              </Button>
            </Grid>
          </Grid>
        </Fade>
      </Modal>
    );
  };

  render() {
    const { load, events, begin, currentLanguage, currentView, showDetail, showEdit, snackbar, dialogOpenMsgDelete, rowToDelete } = this.state;
    const { t, i18n } = this.props;
    const lang = mapToMomentLocale(i18n.language);
    const messages = calendarMessages[lang] || calendarMessages.en;

    if (load) {
      return (
        <Card>
          <CardContent style={{ textAlign: 'center' }}>
            <CircularProgress size={24} />
            <Typography variant="h6">{t ? t('maintenance.activities.loading') : 'Cargando Información...'}</Typography>
          </CardContent>
        </Card>
      );
    }
 
    const eventsToDisplay = currentView === 'agenda' 
    ? [...events]
        .sort((a, b) => b.start.getTime() - a.start.getTime())
        .map(e => ({ ...e, end: e.start })) 
    : events;


    // En el render, antes del return:
    const agendaStartDate = currentView === 'agenda' 
    ? moment().startOf('year').toDate() // O una fecha muy antigua
    : begin;

    return (
      <Card>
        <CardContent>
          <Calendar
            localizer={localizer}
            events={eventsToDisplay}
            startAccessor="start"
            endAccessor="end"
            titleAccessor="title"
            date={agendaStartDate}
            view={currentView} // <-- Controlamos la vista desde el estado
            onView={this.handleViewChange} // <-- Escuchamos cuando el usuario cambia de vista
            onNavigate={this.handleNavigate}
            defaultView="week"
            messages={messages}
            culture={currentLanguage}
            length={300}
            components={{
              event: CustomEvent,
              agenda: {
                event: (props) => (
                  <CustomAgendaEvent 
                    {...props} 
                    onEdit={this.handleOpenEdit}
                    onDelete={(ev) => this.setState({ dialogOpenMsgDelete: true, rowToDelete: ev })}
                    t={t}
                  />
                ),
                time: CustomAgendaTime, // Aquí mostramos el rango unificado
                date: () => null, // Lo anulamos aquí
              },
            }}
            eventPropGetter={eventStyleGetter}
            onSelectEvent={this.handleOpenDetail}
            style={{ height: 600 }}
          />
          {/* --- COMPONENTE MODAL --- */}
          {this.renderModalContent()}
          {this.renderEditModal()}
          <Snackbar 
            open={snackbar.open} 
            autoHideDuration={5000} 
            onClose={this.handleCloseSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <MuiAlert 
              elevation={6} 
              variant="filled" 
              severity={snackbar.severity} 
              onClose={this.handleCloseSnackbar}
            >
              {snackbar.message}
            </MuiAlert>
          </Snackbar>
          <Dialog
            open={dialogOpenMsgDelete}
            onClose={() => this.setState({ dialogOpenMsgDelete: false, rowToDelete: null })}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              {this.props.t ? this.props.t('history.dialog.confirmTitle') : '¿Confirmar eliminación?'}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                {this.props.t 
                  ? this.props.t('history.dialog.confirmText') 
                  : '¿Está seguro de que desea eliminar este mantenimiento? Esta acción no se puede deshacer.'}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              {/* Botón Cancelar */}
              <Button 
                onClick={() => this.setState({ dialogOpenMsgDelete: false, rowToDelete: null })} 
                color="secondary"
              >
                {this.props.t ? this.props.t('history.dialog.cancel') : 'Cancelar'}
              </Button>
              
              {/* Botón Confirmar */}
              <Button 
                onClick={() => {
                  // Ejecutamos tu función remove pasando la fila guardada
                  //this.remove(rowToDelete);
                  this.handleSaved(rowToDelete, 'Cancelado')
                  // Cerramos el diálogo
                  this.setState({ dialogOpenMsgDelete: false, rowToDelete: null });
                }} 
                color="primary"
                variant="contained"
                autoFocus
              >
                {this.props.t ? this.props.t('history.dialog.confirm') : 'Eliminar'}
              </Button>
            </DialogActions>
          </Dialog> 
        </CardContent>
      </Card>
    );
  }
}

export default compose(
  withWidth(),
  withStyles(styles),
  withTranslation()
)(Activities);